import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import clsx from 'clsx'
import { ChevronDown, ChevronRight, Plus, Trash2 } from 'lucide-react'

import type { ScriptEpisodeSummary } from '@/api/studio-production'
import {
  formatEpisodeLabel,
  groupEpisodesBySeason,
  resolveEpisodesPerSeason,
  seasonIndex,
} from '@/features/studio/script/season'
import { ProtoBtn, ProtoInput } from '@/features/studio/components/ui-kit'
import { Button } from '@/shared/components/ui/button'

export type EditorTreeSelection =
  | { kind: 'series' }
  | { kind: 'story' }
  | { kind: 'season'; seasonNo: number }
  | { kind: 'episode'; episodeNo: number }
  | { kind: 'scene'; episodeNo: number; sceneNo: number }

export function treeSelectionEpisodeNo(sel: EditorTreeSelection): number | undefined {
  if (sel.kind === 'episode' || sel.kind === 'scene') return sel.episodeNo
  return undefined
}

export function isSeriesOutlineSelection(sel: EditorTreeSelection): boolean {
  return sel.kind === 'series'
}

export function isStoryContentSelection(sel: EditorTreeSelection): boolean {
  return sel.kind === 'story'
}

export function ScriptEditorTree({
  episodes,
  episodeTarget,
  episodesPerSeasonStored,
  currentEpisodeNo,
  scenesByEpisode,
  selection,
  onSelect,
  onNavigateEpisode,
  canManage,
  canManageEpisodes,
  canManageScenes,
  onAddEpisode,
  onAddSeason,
  onDeleteEpisode,
  onAddScene,
  onDeleteScene,
  onRenameEpisode,
}: {
  episodes: ScriptEpisodeSummary[]
  episodeTarget: number
  episodesPerSeasonStored: number
  currentEpisodeNo: number
  scenesByEpisode: Record<number, { scene_no: number; heading: string }[]>
  selection: EditorTreeSelection
  onSelect: (sel: EditorTreeSelection) => void
  onNavigateEpisode: (episodeNo: number) => void
  canManage: boolean
  /** 默认同 canManage；可单独关闭分集增删而保留场次管理 */
  canManageEpisodes?: boolean
  canManageScenes?: boolean
  onAddEpisode: () => void
  onAddSeason?: () => void
  onDeleteEpisode: (episodeNo: number) => void
  onAddScene: (episodeNo: number) => void
  onDeleteScene: (episodeNo: number, sceneNo: number) => void
  onRenameEpisode?: (episodeNo: number, title: string) => void
}) {
  const manageEpisodes = canManageEpisodes ?? canManage
  const manageScenes = canManageScenes ?? canManage
  const { t } = useTranslation()
  const [expandedSeasons, setExpandedSeasons] = useState<Set<number>>(() => new Set([1]))
  const [expandedEpisodes, setExpandedEpisodes] = useState<Set<number>>(() => new Set([currentEpisodeNo]))
  const treeScrollRef = useRef<HTMLDivElement>(null)
  const sceneItemRefs = useRef<Record<string, HTMLDivElement | null>>({})
  const episodeItemRefs = useRef<Record<number, HTMLDivElement | null>>({})

  const perSeason = resolveEpisodesPerSeason(episodesPerSeasonStored, episodeTarget)

  function scrollEpisodeToTop(episodeNo: number) {
    const container = treeScrollRef.current
    const item = episodeItemRefs.current[episodeNo]
    if (!container || !item) return
    const top = item.getBoundingClientRect().top - container.getBoundingClientRect().top + container.scrollTop
    container.scrollTo({ top: Math.max(0, top), behavior: 'smooth' })
  }

  const selectionKind = selection.kind
  const selectionEpisodeNo = treeSelectionEpisodeNo(selection) ?? null
  const selectionSceneNo = selection.kind === 'scene' ? selection.sceneNo : null
  const selectionSeasonNo = selection.kind === 'season' ? selection.seasonNo : null

  useEffect(() => {
    const seasonNo =
      selectionSeasonNo ?? (selectionEpisodeNo != null ? seasonIndex(selectionEpisodeNo, perSeason) : null)
    if (seasonNo != null) {
      setExpandedSeasons((prev) => {
        if (prev.has(seasonNo)) return prev
        const next = new Set(prev)
        next.add(seasonNo)
        return next
      })
    }

    setExpandedEpisodes((prev) => {
      const needed = new Set<number>([currentEpisodeNo])
      if (selectionEpisodeNo != null) needed.add(selectionEpisodeNo)
      let changed = false
      const next = new Set(prev)
      for (const n of needed) {
        if (!next.has(n)) {
          next.add(n)
          changed = true
        }
      }
      return changed ? next : prev
    })

    if (selectionEpisodeNo != null) {
      requestAnimationFrame(() => scrollEpisodeToTop(selectionEpisodeNo))
    }
  }, [selectionKind, selectionEpisodeNo, selectionSceneNo, selectionSeasonNo, currentEpisodeNo, perSeason])

  const grouped = useMemo(
    () =>
      groupEpisodesBySeason(
        episodes.map((ep) => ({ episode_no: ep.episode_no })),
        episodesPerSeasonStored,
        episodeTarget,
      ).map((group) => ({
        seasonNo: group.seasonNo,
        items: group.items.map(
          (rel) => episodes.find((e) => e.episode_no === rel.episode_no) ?? episodes[0]!,
        ),
      })),
    [episodes, episodesPerSeasonStored, episodeTarget],
  )

  function toggleSeason(seasonNo: number) {
    setExpandedSeasons((prev) => {
      const next = new Set(prev)
      if (next.has(seasonNo)) next.delete(seasonNo)
      else next.add(seasonNo)
      return next
    })
  }

  function toggleEpisode(episodeNo: number) {
    setExpandedEpisodes((prev) => {
      const next = new Set(prev)
      if (next.has(episodeNo)) next.delete(episodeNo)
      else next.add(episodeNo)
      return next
    })
  }

  function isSelected(sel: EditorTreeSelection): boolean {
    if (selection.kind !== sel.kind) return false
    if (sel.kind === 'series' || sel.kind === 'story') return true
    if (sel.kind === 'season') return selection.kind === 'season' && selection.seasonNo === sel.seasonNo
    if (sel.kind === 'episode') return selection.kind === 'episode' && selection.episodeNo === sel.episodeNo
    return (
      selection.kind === 'scene' &&
      selection.episodeNo === sel.episodeNo &&
      selection.sceneNo === sel.sceneNo
    )
  }

  function isSeasonContext(seasonNo: number): boolean {
    if (selection.kind === 'season') return selection.seasonNo === seasonNo
    if (selection.kind === 'episode' || selection.kind === 'scene') {
      return seasonIndex(selection.episodeNo, perSeason) === seasonNo
    }
    return false
  }

  function isEpisodeActive(episodeNo: number): boolean {
    return selection.kind === 'episode' && selection.episodeNo === episodeNo
  }

  function isEpisodeContext(episodeNo: number): boolean {
    if (selection.kind === 'episode' && selection.episodeNo === episodeNo) return true
    return selection.kind === 'scene' && selection.episodeNo === episodeNo
  }

  function renderItem(
    label: string,
    active: boolean,
    onClick: () => void,
    opts?: { indent?: number; trailing?: ReactNode; refKey?: string; episodeRefNo?: number; context?: boolean },
  ) {
    const indent = opts?.indent ?? 0
    const context = opts?.context ?? false
    return (
      <div
        ref={(node) => {
          if (opts?.refKey) sceneItemRefs.current[opts.refKey] = node
          if (opts?.episodeRefNo != null) episodeItemRefs.current[opts.episodeRefNo] = node
        }}
        className="flex items-center gap-0.5"
        style={{ paddingLeft: `${indent * 12}px` }}
      >
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={onClick}
          className={clsx(
            'h-auto min-w-0 flex-1 justify-start rounded-lg px-2 py-1.5 text-left text-[11px]',
            active
              ? 'bg-indigo-500/15 text-indigo-100 ring-1 ring-indigo-500/25'
              : context
                ? 'bg-indigo-500/8 text-indigo-200/80 ring-1 ring-indigo-500/12'
                : 'text-zinc-400 hover:bg-white/[0.04] hover:text-zinc-200',
          )}
        >
          {label}
        </Button>
        {opts?.trailing}
      </div>
    )
  }

  return (
    <aside className="flex w-60 shrink-0 flex-col border-r border-white/[0.06] bg-[#0b0b13]">
      <div className="flex items-center justify-between border-b border-white/[0.04] px-3 py-2">
        <p className="text-[10px] font-semibold uppercase tracking-wider text-zinc-600">
          {t('script.treeLabel')}
        </p>
        {manageEpisodes ? (
          <Button
            type="button"
            variant="ghost"
            size="icon-xs"
            title={t('script.addEpisode')}
            onClick={onAddEpisode}
          >
            <Plus className="h-3.5 w-3.5" />
          </Button>
        ) : null}
      </div>

      <div ref={treeScrollRef} className="studio-scrollbar-hidden flex-1 overflow-y-auto p-2">
        {renderItem(t('script.treeSeries'), isSelected({ kind: 'series' }), () => onSelect({ kind: 'series' }))}
        {renderItem(t('script.treeStory'), isSelected({ kind: 'story' }), () => onSelect({ kind: 'story' }))}

        <p className="mt-3 px-2 pb-1 text-[10px] font-semibold uppercase tracking-wider text-zinc-600">
          {t('script.treeEpisodeSection')}
        </p>

        {grouped.map((group) => {
          const seasonOpen = expandedSeasons.has(group.seasonNo)
          const seasonLabel = t('script.seasonTitle', { season: group.seasonNo })
          const seasonActive = isSelected({ kind: 'season', seasonNo: group.seasonNo })
          const seasonContext = isSeasonContext(group.seasonNo) && !seasonActive

          return (
            <div key={group.seasonNo} className="mt-1">
              <div className="flex items-center gap-0.5">
                <Button
                  type="button"
                  variant="ghost"
                  size="icon-xs"
                  className="h-auto p-0.5 text-zinc-600 hover:text-zinc-400"
                  onClick={() => toggleSeason(group.seasonNo)}
                >
                  {seasonOpen ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className={clsx(
                    'h-auto min-w-0 flex-1 justify-start rounded-lg px-2 py-1 text-left text-[11px] font-medium',
                    seasonActive
                      ? 'bg-indigo-500/15 text-indigo-100 ring-1 ring-indigo-500/25'
                      : seasonContext
                        ? 'bg-indigo-500/8 text-indigo-200/80 ring-1 ring-indigo-500/12'
                        : 'text-zinc-300 hover:bg-white/[0.03]',
                  )}
                  onClick={() => {
                    setExpandedSeasons((prev) => new Set(prev).add(group.seasonNo))
                    onSelect({ kind: 'season', seasonNo: group.seasonNo })
                  }}
                >
                  <span className="truncate">{seasonLabel}</span>
                </Button>
              </div>

              {seasonOpen
                ? group.items.map((ep) => {
                    const epOpen = expandedEpisodes.has(ep.episode_no)
                    const epLabel = formatEpisodeLabel(ep.episode_no, episodesPerSeasonStored, episodeTarget)
                    const customTitle = ep.title?.trim()
                    const epScenes = scenesByEpisode[ep.episode_no] ?? []

                    return (
                      <div key={ep.episode_no} className="mt-0.5">
                        <div className="flex items-center gap-0.5 pl-3">
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon-xs"
                            className="h-auto p-0.5 text-zinc-600 hover:text-zinc-400"
                            onClick={() => toggleEpisode(ep.episode_no)}
                          >
                            {epOpen ? (
                              <ChevronDown className="h-3 w-3" />
                            ) : (
                              <ChevronRight className="h-3 w-3" />
                            )}
                          </Button>
                          {manageEpisodes && onRenameEpisode ? (
                            <div
                              ref={(node) => {
                                episodeItemRefs.current[ep.episode_no] = node
                              }}
                              className={clsx(
                                'min-w-0 flex-1 rounded-lg px-1 py-0.5 transition',
                                isEpisodeActive(ep.episode_no)
                                  ? 'bg-indigo-500/15 ring-1 ring-indigo-500/25'
                                  : isEpisodeContext(ep.episode_no)
                                    ? 'bg-indigo-500/8 ring-1 ring-indigo-500/12'
                                    : '',
                              )}
                            >
                              <ProtoInput
                                value={ep.title ?? ''}
                                onChange={(e) => onRenameEpisode(ep.episode_no, e.target.value)}
                                onFocus={() => {
                                  onSelect({ kind: 'episode', episodeNo: ep.episode_no })
                                  onNavigateEpisode(ep.episode_no)
                                }}
                                placeholder={epLabel}
                                maxLength={255}
                                className="!h-7 !border-transparent !bg-transparent !px-2 !text-[11px] hover:!border-white/10 focus:!border-indigo-500/40"
                              />
                              {customTitle ? (
                                <p className="px-2 text-[10px] text-zinc-600">{epLabel}</p>
                              ) : null}
                            </div>
                          ) : (
                            renderItem(
                              customTitle || epLabel,
                              isEpisodeActive(ep.episode_no),
                              () => {
                                onSelect({ kind: 'episode', episodeNo: ep.episode_no })
                                onNavigateEpisode(ep.episode_no)
                              },
                              {
                                indent: 0,
                                episodeRefNo: ep.episode_no,
                                context:
                                  isEpisodeContext(ep.episode_no) && !isEpisodeActive(ep.episode_no),
                                trailing:
                                  manageEpisodes && episodes.length > 1 ? (
                                    <Button
                                      type="button"
                                      variant="ghost"
                                      size="icon-xs"
                                      className="text-zinc-600 hover:bg-rose-500/10 hover:text-rose-300"
                                      title={t('script.deleteEpisode')}
                                      onClick={(e) => {
                                        e.stopPropagation()
                                        onDeleteEpisode(ep.episode_no)
                                      }}
                                    >
                                      <Trash2 className="h-3 w-3" />
                                    </Button>
                                  ) : null,
                              },
                            )
                          )}
                          {manageEpisodes && onRenameEpisode && episodes.length > 1 ? (
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon-xs"
                              className="text-zinc-600 hover:bg-rose-500/10 hover:text-rose-300"
                              title={t('script.deleteEpisode')}
                              onClick={(e) => {
                                e.stopPropagation()
                                onDeleteEpisode(ep.episode_no)
                              }}
                            >
                              <Trash2 className="h-3 w-3" />
                            </Button>
                          ) : null}
                        </div>

                        {epOpen ? (
                          <div className="mt-0.5 space-y-0.5 pl-6">
                            {epScenes.length === 0 ? (
                              <p className="px-2 py-1 text-[10px] text-zinc-600">{t('script.scenesEmpty')}</p>
                            ) : (
                              epScenes.map((sc) => (
                                <div key={`${ep.episode_no}-${sc.scene_no}`}>
                                  {renderItem(
                                    `${sc.scene_no} ${sc.heading || t('script.sceneUntitled')}`,
                                    isSelected({
                                      kind: 'scene',
                                      episodeNo: ep.episode_no,
                                      sceneNo: sc.scene_no,
                                    }),
                                    () =>
                                      onSelect({
                                        kind: 'scene',
                                        episodeNo: ep.episode_no,
                                        sceneNo: sc.scene_no,
                                      }),
                                    {
                                      indent: 1,
                                      refKey: `${ep.episode_no}-${sc.scene_no}`,
                                      trailing:
                                        manageScenes ? (
                                          <Button
                                            type="button"
                                            variant="ghost"
                                            size="icon-xs"
                                            className="text-zinc-600 hover:bg-rose-500/10 hover:text-rose-300"
                                            title={t('script.deleteScene')}
                                            onClick={(e) => {
                                              e.stopPropagation()
                                              onDeleteScene(ep.episode_no, sc.scene_no)
                                            }}
                                          >
                                            <Trash2 className="h-3 w-3" />
                                          </Button>
                                        ) : null,
                                    },
                                  )}
                                </div>
                              ))
                            )}
                            {manageScenes ? (
                              <ProtoBtn
                                size="sm"
                                variant="ghost"
                                className="!mt-1 w-full !justify-start !px-2 !py-1 !text-[10px]"
                                onClick={() => onAddScene(ep.episode_no)}
                              >
                                <Plus className="h-3 w-3" />
                                {t('script.addScene')}
                              </ProtoBtn>
                            ) : null}
                          </div>
                        ) : null}
                      </div>
                    )
                  })
                : null}
            </div>
          )
        })}

        {manageEpisodes && onAddSeason ? (
          <ProtoBtn
            size="sm"
            variant="ghost"
            className="!mt-2 w-full !justify-start !px-2 !py-1 !text-[10px]"
            onClick={onAddSeason}
          >
            <Plus className="h-3 w-3" />
            {t('script.addSeason')}
          </ProtoBtn>
        ) : null}
      </div>
    </aside>
  )
}
